import { Modal } from '@arco-design/web-react';
import ModalDangerIcon from '@dmp/assets/icons/danger.svg?react';
import ModalInfoIcon from '@dmp/assets/icons/info.svg?react';
import ModalSuccessIcon from '@dmp/assets/icons/modal-success.svg?react';
import ModalProgressIcon from '@dmp/assets/icons/progress.svg?react';

import './ModalService.css';
import {
  type ConfirmModalProps,
  type ErrorModalProps,
  type InfoModalProps,
  type ProgressModalProps,
  type SuccessModalProps,
  type WarningModalProps,
} from './ModalService.types';

const DEFAULT_DURATION = 5000;
const REDIRECT_DURATION = 10000;

export const ModalService = {
  confirm: (props: ConfirmModalProps) => {
    return Modal.confirm({
      ...props,
      icon: props.icon ?? <ModalDangerIcon />,
      okText: props.okText ?? 'OK',
      okButtonProps: props.okButtonProps ?? {
        type: 'primary',
      },
      cancelText: props.cancelText ?? 'Cancel',
      className: props.className ? `dmp-modal-confirm ${props.className}` : 'dmp-modal-confirm',
    });
  },

  info: (props: InfoModalProps) => {
    const { onAutoDisappear, duration, persistent, ...rest } = props;

    let closeTimer: number | null = null;

    const modal = Modal.info({
      ...rest,
      icon: props.icon ?? <ModalInfoIcon />,
      okText: props.okText ?? 'OK',
      okButtonProps: props.okButtonProps ?? {
        type: 'default',
      },
      className: props.className ? `dmp-modal-info ${props.className}` : 'dmp-modal-info',
      afterClose: () => {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
        props.afterClose?.();
      },
    });

    if (!persistent) {
      closeTimer = window.setTimeout(() => {
        modal.close();
        onAutoDisappear?.();
      }, duration ?? DEFAULT_DURATION);
    }

    return modal;
  },

  success: (props: SuccessModalProps) => {
    const { onAutoDisappear, duration, persistent, ...rest } = props;

    let closeTimer: number | null = null;

    const modalDuration = duration
      ? duration
      : props.purpose === 'redirect'
        ? REDIRECT_DURATION // redirect to landing page after 10 seconds
        : DEFAULT_DURATION;

    const modal = Modal.success({
      ...rest,
      icon: props.icon ?? <ModalSuccessIcon />,
      okText: props.okText ?? 'OK',
      className: props.className ? `dmp-modal-success ${props.className}` : 'dmp-modal-success',
      okButtonProps: props.okButtonProps ?? {
        type: 'default',
      },
      // after user close the modal manually, we need to clear the timer
      afterClose: () => {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
        props.afterClose?.();
      },
    });

    if (!persistent) {
      closeTimer = window.setTimeout(() => {
        modal.close();
        onAutoDisappear?.();
      }, modalDuration);
    }

    return modal;
  },

  warning: (props: WarningModalProps) => {
    const { onAutoDisappear, duration, persistent, ...rest } = props;

    let closeTimer: number | null = null;

    const modal = Modal.warning({
      ...rest,
      icon: props.icon ?? <ModalInfoIcon fill="#FF7601" />,
      okText: props.okText ?? 'OK',
      okButtonProps: props.okButtonProps ?? {
        type: 'default',
      },
      className: props.className ? `dmp-modal-warning ${props.className}` : 'dmp-modal-warning',
      afterClose: () => {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
        props.afterClose?.();
      },
    });

    if (!persistent) {
      closeTimer = window.setTimeout(() => {
        modal.close();
        onAutoDisappear?.();
      }, duration ?? DEFAULT_DURATION);
    }

    return modal;
  },

  progress: (props: ProgressModalProps) => {
    const { onAutoDisappear, duration, persistent, ...rest } = props;

    let closeTimer: number | null = null;

    const modal = Modal.warning({
      ...rest,
      icon: props.icon ?? <ModalProgressIcon />,
      okText: props.okText ?? 'OK',
      okButtonProps: props.okButtonProps ?? {
        type: 'default',
      },
      className: props.className ? `dmp-modal-progress ${props.className}` : 'dmp-modal-progress',
      afterClose: () => {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
        props.afterClose?.();
      },
    });

    if (!persistent) {
      closeTimer = window.setTimeout(() => {
        modal.close();
        onAutoDisappear?.();
      }, duration ?? DEFAULT_DURATION);
    }

    return modal;
  },

  error: (props: ErrorModalProps) => {
    // default to persistent modal, let user decide to close the modal manually
    const { onAutoDisappear, duration, persistent = true, ...rest } = props;

    let closeTimer: number | null = null;

    const modal = Modal.error({
      ...rest,
      icon: props.icon ?? <ModalDangerIcon />,
      okText: props.okText ?? 'OK',
      okButtonProps: props.okButtonProps ?? {
        type: 'default',
      },
      className: props.className ? `dmp-modal-error ${props.className}` : 'dmp-modal-error',
      afterClose: () => {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
        props.afterClose?.();
      },
    });

    if (!persistent) {
      closeTimer = window.setTimeout(() => {
        modal.close();
        onAutoDisappear?.();
      }, duration ?? DEFAULT_DURATION);
    }

    return modal;
  },

  config: (config: Parameters<typeof Modal.config>[0]) => {
    return Modal.config(config);
  },

  destroyAll: () => {
    return Modal.destroyAll();
  },
};
